﻿import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import * as THREE from "three";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls.js";
import gsap from "gsap";
import { Activity, Clock3, Layers3, Network, Play, RotateCcw, ShieldAlert, Target, ZoomIn, ZoomOut, ArrowRight } from "lucide-react";

const cleanTitle = (title) => {
  if (!title) return "";
  // Split only on a spaced dash (a real " - " subtitle separator) or a parenthetical,
  // so intra-word hyphens like "Multi-VM" / "CCO-09" are preserved.
  const parts = title.split(/\s+-\s+|\s*[–—(]\s*/);
  return parts[0].trim();
};

const SCENE_BG = 0x060b14;
const PANEL_BG =
  "linear-gradient(180deg, rgba(8, 14, 26, 0.94), rgba(3, 8, 18, 0.98))";

const LABEL_HIDE_DISTANCE = {
  1: 38,
  2: 22,
  3: 16
};

const categoryProfiles = [
  {
    match: ["scywf", "workforce"],
    badge: "SCyWF",
    summary: "Role-based workforce tracks mapped to specialty areas and operational tasks.",
    outcome: "Workforce readiness"
  },
  {
    match: ["ecsf", "european"],
    badge: "ECSF",
    summary: "European cybersecurity role training with guided labs and skills evidence.",
    outcome: "Role capability"
  },
  {
    match: ["nice"],
    badge: "NICE",
    summary: "NICE framework pathways aligned to work roles, knowledge, and skills.",
    outcome: "Framework alignment"
  },
  {
    match: ["homeland", "exercise"],
    badge: "HSEEP",
    summary: "Exercise and evaluation tracks for preparedness, response, and reporting.",
    outcome: "Exercise maturity"
  },
  {
    match: ["build your own"],
    badge: "CUSTOM",
    summary: "On-demand scenario creation from selected topics, vulnerabilities, and topologies.",
    outcome: "Custom lab launch"
  },
  {
    match: ["emerging"],
    badge: "THREAT",
    summary: "Current threat labs focused on recent attack patterns and vulnerability response.",
    outcome: "Threat readiness"
  },
  {
    match: ["learning paths", "cyberedu"],
    badge: "CORE",
    summary: "Guided cyber range curriculum organized into progressive learning paths.",
    outcome: "Path progression"
  },
  {
    match: ["multi-vm"],
    badge: "MULTI-VM LAB",
    summary: "Advanced cybersecurity labs featuring multiple virtual machines with realistic pivot scenarios.",
    outcome: "Multi-VM deployment"
  }
];

const getCategoryProfile = (title = "") => {
  const normalizedTitle = title.toLowerCase();
  return (
    categoryProfiles.find((profile) =>
      profile.match.some((term) => normalizedTitle.includes(term))
    ) || {
      badge: "TRACK",
      summary: "Structured cyber range content grouped by role, objective, and practice area.",
      outcome: "Skill growth"
    }
  );
};

const parseHours = (value) => {
  if (!value) return 0;
  const match = String(value).match(/(\d+(?:\.\d+)?)/);
  return match ? Number(match[1]) : 0;
};

const makeReadinessScore = (node, scenarioCount, childCount) => {
  const seed = String(node.id || node.title || "")
    .split("")
    .reduce((sum, char) => sum + char.charCodeAt(0), 0);
  const base = node.level === 0 ? 74 : 58 + ((seed + scenarioCount * 7 + childCount * 5) % 31);
  return Math.max(42, Math.min(96, base));
};

const createRoundedBoxGeometry = (width, height, depth, radius, segments = 3) => {
  const shape = new THREE.Shape();
  const x = -width / 2;
  const y = -height / 2;

  shape.moveTo(x + radius, y);
  shape.lineTo(x + width - radius, y);
  shape.quadraticCurveTo(x + width, y, x + width, y + radius);
  shape.lineTo(x + width, y + height - radius);
  shape.quadraticCurveTo(x + width, y + height, x + width - radius, y + height);
  shape.lineTo(x + radius, y + height);
  shape.quadraticCurveTo(x, y + height, x, y + height - radius);
  shape.lineTo(x, y + radius);
  shape.quadraticCurveTo(x, y, x + radius, y);

  return new THREE.ExtrudeGeometry(shape, {
    depth,
    bevelEnabled: true,
    bevelSegments: segments,
    bevelSize: radius * 0.45,
    bevelThickness: radius * 0.45,
    curveSegments: 10
  }).center();
};

// Canvas texture generator for holographic icons inside glass structures
const createIconTexture = (iconType, glowColorHex) => {
  const canvas = document.createElement("canvas");
  canvas.width = 256;
  canvas.height = 256;
  const ctx = canvas.getContext("2d");

  ctx.clearRect(0, 0, 256, 256);

  ctx.shadowColor = glowColorHex;
  ctx.shadowBlur = 18;
  ctx.strokeStyle = glowColorHex;
  ctx.lineWidth = 10;
  ctx.lineJoin = "round";
  ctx.lineCap = "round";

  const w = 256;
  const h = 256;

  if (iconType === "shield") {
    ctx.beginPath();
    ctx.moveTo(w / 2, h / 6);
    ctx.lineTo(w * 0.78, h * 0.28);
    ctx.lineTo(w * 0.78, h * 0.6);
    ctx.quadraticCurveTo(w / 2, h * 0.86, w * 0.22, h * 0.6);
    ctx.lineTo(w * 0.22, h * 0.28);
    ctx.closePath();
    ctx.stroke();

    ctx.lineWidth = 5;
    ctx.beginPath();
    ctx.moveTo(w / 2, h * 0.26);
    ctx.lineTo(w * 0.68, h * 0.33);
    ctx.lineTo(w * 0.68, h * 0.56);
    ctx.quadraticCurveTo(w / 2, h * 0.78, w * 0.32, h * 0.56);
    ctx.lineTo(w * 0.32, h * 0.33);
    ctx.closePath();
    ctx.stroke();
  } else if (iconType === "scale") {
    ctx.beginPath();
    ctx.moveTo(w / 2, h * 0.22);
    ctx.lineTo(w / 2, h * 0.78);
    ctx.moveTo(w * 0.38, h * 0.78);
    ctx.lineTo(w * 0.62, h * 0.78);
    ctx.stroke();

    ctx.beginPath();
    ctx.moveTo(w * 0.26, h * 0.32);
    ctx.lineTo(w * 0.74, h * 0.32);
    ctx.stroke();

    ctx.beginPath();
    ctx.moveTo(w * 0.26, h * 0.32);
    ctx.lineTo(w * 0.18, h * 0.58);
    ctx.lineTo(w * 0.34, h * 0.58);
    ctx.closePath();
    ctx.stroke();

    ctx.beginPath();
    ctx.arc(w * 0.26, h * 0.62, w * 0.08, Math.PI, 0);
    ctx.stroke();

    ctx.beginPath();
    ctx.moveTo(w * 0.74, h * 0.32);
    ctx.lineTo(w * 0.66, h * 0.58);
    ctx.lineTo(w * 0.82, h * 0.58);
    ctx.closePath();
    ctx.stroke();

    ctx.beginPath();
    ctx.arc(w * 0.74, h * 0.62, w * 0.08, Math.PI, 0);
    ctx.stroke();
  } else if (iconType === "factory") {
    ctx.beginPath();
    ctx.moveTo(w * 0.16, h * 0.75);
    ctx.lineTo(w * 0.84, h * 0.75);
    ctx.lineTo(w * 0.84, h * 0.48);
    ctx.lineTo(w * 0.72, h * 0.6);
    ctx.lineTo(w * 0.72, h * 0.48);
    ctx.lineTo(w * 0.6, h * 0.6);
    ctx.lineTo(w * 0.6, h * 0.48);
    ctx.lineTo(w * 0.48, h * 0.6);
    ctx.lineTo(w * 0.48, h * 0.42);
    ctx.lineTo(w * 0.26, h * 0.42);
    ctx.lineTo(w * 0.26, h * 0.3);
    ctx.lineTo(w * 0.16, h * 0.3);
    ctx.closePath();
    ctx.stroke();

    ctx.lineWidth = 5;
    ctx.beginPath();
    ctx.moveTo(w * 0.21, h * 0.24);
    ctx.quadraticCurveTo(w * 0.17, h * 0.18, w * 0.23, h * 0.12);
    ctx.stroke();
  } else if (iconType === "root") {
    ctx.beginPath();
    for (let i = 0; i < 6; i++) {
      const angle = (i * Math.PI) / 3;
      const x = w / 2 + Math.cos(angle) * (w * 0.28);
      const y = h / 2 + Math.sin(angle) * (h * 0.28);
      if (i === 0) ctx.moveTo(x, y);
      else ctx.lineTo(x, y);
    }
    ctx.closePath();
    ctx.stroke();

    ctx.beginPath();
    ctx.arc(w / 2, h / 2, w * 0.12, 0, Math.PI * 2);
    ctx.stroke();
  } else {
    // Scenario command terminal prompt
    ctx.beginPath();
    ctx.moveTo(w * 0.32, h * 0.35);
    ctx.lineTo(w * 0.5, h * 0.5);
    ctx.lineTo(w * 0.32, h * 0.65);
    ctx.moveTo(w * 0.55, h * 0.65);
    ctx.lineTo(w * 0.72, h * 0.65);
    ctx.stroke();
  }

  const texture = new THREE.CanvasTexture(canvas);
  return texture;
};

// Node aesthetic setup matching the professional cyber system
const getNodeTheme = (type, title, difficulty) => {
  const normalizedTitle = (title || "").toLowerCase();
  const normalizedDiff = (difficulty || "").toLowerCase();

  let color = 0x10b981; // default green
  let glowColor = 0x34d399;
  let glowColorHex = "#34d399";
  let typeLabel = "Category";
  let icon = "default";

  if (type === "scenario" || type === "pending_scenario") {
    typeLabel = "Box";
    icon = "scenario";
    if (normalizedDiff.includes("easy") || normalizedDiff.includes("low")) {
      color = 0x10b981;
      glowColor = 0x34d399;
      glowColorHex = "#34d399";
    } else if (normalizedDiff.includes("medium") || normalizedDiff.includes("intermediate")) {
      color = 0xf59e0b;
      glowColor = 0xfacc15;
      glowColorHex = "#facc15";
    } else if (normalizedDiff.includes("hard") || normalizedDiff.includes("high") || normalizedDiff.includes("advanced")) {
      color = 0xef4444;
      glowColor = 0xfca5a5;
      glowColorHex = "#fca5a5";
    } else {
      color = 0x06b6d4;
      glowColor = 0x67e8f9;
      glowColorHex = "#67e8f9";
    }
  } else {
    // Main Tracks
    if (normalizedTitle.includes("protect") || normalizedTitle.includes("defend")) {
      color = 0x10b981; // Emerald
      glowColor = 0x34d399;
      glowColorHex = "#34d399";
      icon = "shield";
    } else if (normalizedTitle.includes("workforce") || normalizedTitle.includes("legal") || normalizedTitle.includes("compliance") || normalizedTitle.includes("governance")) {
      color = 0x06b6d4; // Cyan/Blue
      glowColor = 0x67e8f9;
      glowColorHex = "#67e8f9";
      icon = "scale";
    } else if (normalizedTitle.includes("ics") || normalizedTitle.includes("ot") || normalizedTitle.includes("industrial")) {
      color = 0x8b5cf6; // Purple
      glowColor = 0xa78bfa;
      glowColorHex = "#a78bfa";
      icon = "factory";
    } else {
      // General categories (e.g. Scywf parent)
      color = 0x3b82f6; // Royal Blue
      glowColor = 0x60a5fa;
      glowColorHex = "#60a5fa";
      icon = "scale";
    }
  }

  return { color, glowColor, glowColorHex, typeLabel, icon };
};

// Node dimensions helper to prevent S-curve connection line penetrations
const getExitOffset = (level) => {
  if (level === 0) return 0.2;
  if (level === 1) return 1.15;
  if (level === 2) return 0.5;
  return 0.3;
};

const getEnterOffset = (level) => {
  if (level === 0) return 0.2;
  if (level === 1) return 1.15;
  if (level === 2) return 0.5;
  return 0.3;
};

export default function ThreeNodeNetwork({ treeData, onOpenLab, onRequestCustom }) {
  const navigate = useNavigate();
  const canvasRef = useRef(null);
  const containerRef = useRef(null);

  // States for React rendering (HTML labels overlay)
  const [flatNodes, setFlatNodes] = useState([]);
  const [activeNode, setActiveNode] = useState(null);
  const [hoveredNodeId, setHoveredNodeId] = useState(null);

  // Show a request button in the active-node panel for categories that
  // support on-demand lab/scenario creation (Build Your Own, Emerging Threats
  // custom versions, Multi-VM Labs). Routes to the page's existing modals.
  const showCustomRequestButton = useMemo(() => {
    if (!activeNode) return false;
    if (activeNode.type !== "category") return false;

    const path = [];
    let curr = activeNode;
    while (curr) {
      path.unshift(curr);
      curr = curr.parentId ? flatNodes.find((n) => n.id === curr.parentId) : null;
    }

    const normalizedTitles = path.map((item) =>
      String(item?.title || "")
        .trim()
        .toLowerCase(),
    );
    const lastTitle = normalizedTitles[normalizedTitles.length - 1] || "";

    const isEmergingThreatCustomVersions =
      normalizedTitles.some((title) => title.includes("emerging threats")) &&
      lastTitle.includes("custom versions");

    const isBuildYourOwn = lastTitle.includes("build your own");

    const isMultiVMLabs = lastTitle.includes("multi-vm");

    return isEmergingThreatCustomVersions || isBuildYourOwn || isMultiVMLabs;
  }, [activeNode, flatNodes]);

  const isMultiVMLabs = useMemo(() => {
    return activeNode && String(activeNode.title || "").toLowerCase().includes("multi-vm");
  }, [activeNode]);

  const isBuildYourOwn = useMemo(() => {
    return (
      activeNode &&
      String(activeNode.title || "")
        .toLowerCase()
        .includes("build your own")
    );
  }, [activeNode]);

  // Refs for WebGL access to prevent React scene teardowns
  const nodesRef = useRef([]);
  const hoveredNodeIdRef = useRef(null);
  const activeNodeIdRef = useRef(null);
  // Perf: cache of label DOM elements keyed by nodeId, rebuilt only when the
  // label set changes -- avoids a querySelector + CSS.escape per node per frame.
  const labelElementsRef = useRef(new Map());
  // Perf: parentId -> node lookup, rebuilt with the node set -- avoids an O(n)
  // .find() per node per frame in the connection-line pass.
  const nodeByIdRef = useRef(new Map());
  const threeObjectsRef = useRef({
    scene: null,
    camera: null,
    renderer: null,
    controls: null,
    nodeGroup: null,
    connectionGroup: null,
    particleGroup: null
  });

  // 1. Recursive Tree Coordinate Builder (local parent-relative spacing model)
  useEffect(() => {
    if (!treeData) return;

    const buildHierarchy = (node, parentId = null) => {
      const id = node.id || node.slug || node.title || `node-${Math.random().toString(36).substr(2, 9)}`;
      const children = (node.children || []).map((c) => buildHierarchy(c, id));
      return {
        ...node,
        id,
        parentId,
        children
      };
    };

    const root = buildHierarchy(treeData);
    const result = [];

    // Spacing configuration (keeps layout tight and aligned)
    const assignCoordinates = (n, parentPos = null, index = 0, total = 1, level = 0, parentId = null) => {
      const targetPos = new THREE.Vector3();

      if (level === 0) {
        targetPos.set(0, 9.2, 0);
      } else if (level === 1) {
        // Flat horizontal layout with a shallow upward curve
        const spacing = 5.5; // Spacious horizontal spacing
        const xOffset = (index - (total - 1) / 2) * spacing;
        const yOffset = 1.75 + (Math.abs(xOffset) * 0.13);
        targetPos.set(xOffset, yOffset, 0);
      } else if (level === 2) {
        // Level 2: place below parent, fanning out horizontally with extra vertical clearance
        const spacing = 4.2;
        const xOffset = (index - (total - 1) / 2) * spacing;
        const zOffset = (index % 2 === 0 ? 0.8 : -0.8) * (total > 1 ? 1 : 0);
        targetPos.set(parentPos.x + xOffset, parentPos.y - 6.5, parentPos.z + zOffset);
      } else {
        // Level 3: place below parent, fanning out horizontally with extra clearance
        const spacing = 2.6;
        const xOffset = (index - (total - 1) / 2) * spacing;
        const zOffset = (index % 2 === 0 ? 0.4 : -0.4) * (total > 1 ? 1 : 0);
        targetPos.set(parentPos.x + xOffset, parentPos.y - 5.5, parentPos.z + zOffset);
      }

      const visualNode = {
        id: n.id,
        title: n.title || "Untitled",
        type: n.type || "category",
        difficulty: n.difficulty || null,
        time_estimate: n.time_estimate || null,
        description: n.description || "",
        level,
        parentId,
        index,
        childrenIds: n.children.map((c) => c.id),
        rawNode: n,
        targetPos,
        currentPos: level === 0 || !parentId ? targetPos.clone() : new THREE.Vector3(targetPos.x, parentPos.y, parentPos.z),
        scale: level === 0 ? 1 : 0,
        opacity: level === 0 ? 1 : 0,
        expanded: false,
        visible: level === 0
      };

      result.push(visualNode);

      const children = n.children || [];
      children.forEach((child, idx) => {
        assignCoordinates(child, targetPos, idx, children.length, level + 1, n.id);
      });
    };

    assignCoordinates(root);

    nodesRef.current = result;
    nodeByIdRef.current = new Map(result.map((n) => [n.id, n]));
    setFlatNodes([...result]);

    const rootNode = result.find((n) => n.level === 0);
    if (rootNode) {
      setActiveNode(rootNode);
      activeNodeIdRef.current = rootNode.id;
    }
  }, [treeData]);

  // Perf: rebuild the nodeId -> label-element cache whenever the rendered label
  // set changes. The animation loop then reads elements from this Map instead of
  // running containerRef.current.querySelector(...) for every node every frame
  // (the single biggest hotspot in the profiler -- ~45% of main-thread time).
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const map = new Map();
    container
      .querySelectorAll("[data-node-id]")
      .forEach((el) => map.set(el.getAttribute("data-node-id"), el));
    labelElementsRef.current = map;
  }, [flatNodes]);

  // 2. Initialize WebGL Canvas ONCE (depends ONLY on treeData to avoid resets on state updates)
  useEffect(() => {
    if (!canvasRef.current || nodesRef.current.length === 0) return;

    const width = containerRef.current.clientWidth;
    const height = containerRef.current.clientHeight;

    const textureLoader = new THREE.TextureLoader();
    const logoTexture = textureLoader.load("/brand/quantum-logo.png");

    const scene = new THREE.Scene();
    scene.background = new THREE.Color(SCENE_BG);
    scene.fog = new THREE.FogExp2(SCENE_BG, 0.022);

    const camera = new THREE.PerspectiveCamera(48, width / height, 0.1, 1000);
    camera.position.set(0, 4.5, 23);

    const renderer = new THREE.WebGLRenderer({
      canvas: canvasRef.current,
      antialias: true,
      alpha: true,
      powerPreference: "high-performance"
    });
    renderer.setSize(width, height);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));

    const controls = new OrbitControls(camera, renderer.domElement);
    controls.enableDamping = true;
    controls.dampingFactor = 0.05;
    controls.maxDistance = 58;
    controls.minDistance = 4;
    controls.enableRotate = false; // Lock 3D rotation
    controls.mouseButtons = {
      LEFT: THREE.MOUSE.PAN,
      MIDDLE: THREE.MOUSE.DOLLY,
      RIGHT: THREE.MOUSE.NONE
    };
    controls.touches = {
      ONE: THREE.TOUCH.PAN,
      TWO: THREE.TOUCH.DOLLY_PAN
    };

    const rootNode = nodesRef.current.find((node) => node.level === 0);
    if (rootNode) {
      controls.target.copy(rootNode.targetPos);
      camera.position.set(rootNode.targetPos.x, rootNode.targetPos.y + 2.5, rootNode.targetPos.z + 21);
      controls.update();
    }

    const ambientLight = new THREE.AmbientLight(0x92a4bd, 0.9);
    scene.add(ambientLight);

    const keyLight = new THREE.PointLight(0x7dd3fc, 1.55, 70);
    keyLight.position.set(-12, 12, 18);
    scene.add(keyLight);

    const rimLight = new THREE.PointLight(0x34d399, 0.9, 55);
    rimLight.position.set(14, 7, -12);
    scene.add(rimLight);

    const dirLight = new THREE.DirectionalLight(0xffffff, 0.9);
    dirLight.position.set(10, 22, 12);
    scene.add(dirLight);

    const grid = new THREE.GridHelper(52, 26, 0x17423e, 0x0f2431);
    grid.position.y = -8.9;
    grid.material.transparent = true;
    grid.material.opacity = 0.28;
    scene.add(grid);

    const horizonGrid = new THREE.GridHelper(52, 26, 0x123341, 0x0c1e2a);
    horizonGrid.position.y = -8.85;
    horizonGrid.position.z = -11;
    horizonGrid.rotation.x = Math.PI / 2;
    horizonGrid.material.transparent = true;
    horizonGrid.material.opacity = 0.14;
    scene.add(horizonGrid);

    const nodeGroup = new THREE.Group();
    const connectionGroup = new THREE.Group();
    const particleGroup = new THREE.Group();
    scene.add(nodeGroup);
    scene.add(connectionGroup);
    scene.add(particleGroup);

    threeObjectsRef.current = {
      scene,
      camera,
      renderer,
      controls,
      nodeGroup,
      connectionGroup,
      particleGroup
    };

    const nodeMeshes = [];

    // Perf: every node of a given level uses an identically-shaped geometry, but
    // the original code rebuilt each one per node -- ExtrudeGeometry/EdgesGeometry
    // are expensive and were the top setup hotspots in the profiler (~29% combined).
    // Build each distinct geometry ONCE here and share it across meshes. Geometry
    // can be shared freely in three.js; only materials stay per-node (per-node color).
    // These shared geometries are disposed once in cleanup (see below).
    const level1CoreGeom = createRoundedBoxGeometry(2.28, 2.28, 1.85, 0.18, 4);
    const level1FrameGeom = new THREE.EdgesGeometry(
      createRoundedBoxGeometry(1.86, 1.86, 1.45, 0.12, 2)
    );
    const level2CoreGeom = new THREE.OctahedronGeometry(0.7, 0);
    const level3CoreGeom = createRoundedBoxGeometry(0.86, 0.55, 0.42, 0.06, 2);
    const level0CoreGeom = new THREE.PlaneGeometry(3.0, 3.0);

    // Outline (EdgesGeometry) for each distinct core shape, built once.
    const level1OutlineGeom = new THREE.EdgesGeometry(level1CoreGeom);
    const level2OutlineGeom = new THREE.EdgesGeometry(level2CoreGeom);
    const level3OutlineGeom = new THREE.EdgesGeometry(level3CoreGeom);

    // Shared geometries for the small decorative meshes (same dims for all nodes).
    const root_ring1Geom = new THREE.TorusGeometry(2.1, 0.03, 8, 48);
    const root_ring2Geom = new THREE.TorusGeometry(2.4, 0.02, 8, 48);
    const level1FacePlateGeom = new THREE.PlaneGeometry(1.25, 1.25);
    const level1TorusGeom = new THREE.TorusGeometry(1.78, 0.018, 8, 48);
    const level2RingGeom = new THREE.TorusGeometry(1.0, 0.02, 8, 32);
    const level3ElectronGeom = new THREE.SphereGeometry(0.08, 6, 6);
    const hologramGeomL1 = new THREE.PlaneGeometry(1.3, 1.3);
    const hologramGeomSmall = new THREE.PlaneGeometry(0.65, 0.65);
    const particleGeomL1 = new THREE.SphereGeometry(0.12, 8, 8);
    const particleGeomSmall = new THREE.SphereGeometry(0.08, 8, 8);

    // Collect the shared geometries so cleanup can dispose each exactly once
    // (the scene.traverse disposer is switched off for these to avoid
    // double-disposing a geometry referenced by many meshes).
    const sharedGeometries = [
      level1CoreGeom, level1FrameGeom, level2CoreGeom, level3CoreGeom, level0CoreGeom,
      level1OutlineGeom, level2OutlineGeom, level3OutlineGeom,
      root_ring1Geom, root_ring2Geom, level1FacePlateGeom, level1TorusGeom,
      level2RingGeom, level3ElectronGeom, hologramGeomL1, hologramGeomSmall,
      particleGeomL1, particleGeomSmall
    ];
    const sharedGeometrySet = new Set(sharedGeometries);

    // Create 3D Meshes for Nodes
    nodesRef.current.forEach((node) => {
      const group = new THREE.Group();
      group.position.copy(node.currentPos);
      group.scale.setScalar(node.scale);

      const theme = getNodeTheme(node.type, node.title, node.difficulty);

      let geom;
      let meshMat;

      if (node.level === 0) {
        // Company logo plane for root (facing camera)
        geom = level0CoreGeom;

        meshMat = new THREE.MeshBasicMaterial({
          map: logoTexture,
          transparent: true,
          side: THREE.DoubleSide,
          depthWrite: false
        });

        const ring1 = new THREE.Mesh(
          root_ring1Geom,
          new THREE.MeshBasicMaterial({ color: 0x00ffcc, transparent: true, opacity: 0.3 })
        );
        group.add(ring1);
        node.orbitingRing1 = ring1;

        const ring2 = new THREE.Mesh(
          root_ring2Geom,
          new THREE.MeshBasicMaterial({ color: 0x06b6d4, transparent: true, opacity: 0.2 })
        );
        group.add(ring2);
        node.orbitingRing2 = ring2;

      } else if (node.level === 1) {
        // Glassmorphic cubes for category tracks
        geom = level1CoreGeom;

        meshMat = new THREE.MeshPhysicalMaterial({
          color: theme.color,
          roughness: 0.22,
          metalness: 0.42,
          transparent: true,
          opacity: 0.58,
          transmission: 0.22,
          ior: 1.45,
          thickness: 1.4,
          emissive: theme.glowColor,
          emissiveIntensity: 0.06,
          clearcoat: 0.85,
          clearcoatRoughness: 0.16
        });

        const innerFrame = new THREE.LineSegments(
          level1FrameGeom,
          new THREE.LineBasicMaterial({ color: theme.glowColor, transparent: true, opacity: 0.28 })
        );
        group.add(innerFrame);

        const facePlate = new THREE.Mesh(
          level1FacePlateGeom,
          new THREE.MeshBasicMaterial({
            color: theme.glowColor,
            transparent: true,
            opacity: 0.08,
            side: THREE.DoubleSide,
            depthWrite: false
          })
        );
        facePlate.position.z = 0.94;
        group.add(facePlate);

        const orbitTorus = new THREE.Mesh(
          level1TorusGeom,
          new THREE.MeshBasicMaterial({ color: theme.glowColor, transparent: true, opacity: 0.18 })
        );
        orbitTorus.rotation.x = Math.PI / 2;
        group.add(orbitTorus);
        node.orbitingTorus = orbitTorus;

      } else if (node.level === 2) {
        // Octahedron for sub-categories
        geom = level2CoreGeom;

        meshMat = new THREE.MeshPhysicalMaterial({
          color: theme.color,
          roughness: 0.2,
          metalness: 0.58,
          transparent: true,
          opacity: 0.65,
          transmission: 0.12,
          ior: 1.5,
          thickness: 1.0,
          emissive: theme.glowColor,
          emissiveIntensity: 0.14,
          clearcoat: 0.7
        });

        const ring = new THREE.Mesh(
          level2RingGeom,
          new THREE.MeshBasicMaterial({ color: theme.glowColor, transparent: true, opacity: 0.45 })
        );
        group.add(ring);
        node.orbitingRing = ring;

      } else {
        // Tetrahedron for scenario boxes
        geom = level3CoreGeom;

        meshMat = new THREE.MeshPhysicalMaterial({
          color: theme.color,
          roughness: 0.24,
          metalness: 0.62,
          transparent: true,
          opacity: 0.72,
          transmission: 0.08,
          ior: 1.55,
          thickness: 0.6,
          emissive: theme.glowColor,
          emissiveIntensity: 0.16,
          clearcoat: 0.7
        });

        const electron = new THREE.Mesh(
          level3ElectronGeom,
          new THREE.MeshBasicMaterial({ color: theme.glowColor })
        );
        group.add(electron);
        node.electronMesh = electron;
      }

      const coreMesh = new THREE.Mesh(geom, meshMat);
      coreMesh.userData = { nodeId: node.id };
      group.add(coreMesh);
      node.coreMesh = coreMesh;
      nodeMeshes.push(coreMesh);

      if (node.level > 0) {
        const outlineGeom =
          node.level === 1
            ? level1OutlineGeom
            : node.level === 2
              ? level2OutlineGeom
              : level3OutlineGeom;
        const outline = new THREE.LineSegments(
          outlineGeom,
          new THREE.LineBasicMaterial({ color: theme.glowColor, transparent: true, opacity: 0.75 })
        );
        group.add(outline);
      }

      if (node.level > 0) {
        const innerSymbolTexture = createIconTexture(theme.icon, theme.glowColorHex);
        const hologramPlane = new THREE.Mesh(
          node.level === 1 ? hologramGeomL1 : hologramGeomSmall,
          new THREE.MeshBasicMaterial({
            map: innerSymbolTexture,
            transparent: true,
            opacity: 0.95,
            side: THREE.DoubleSide,
            depthWrite: false,
            blending: THREE.AdditiveBlending
          })
        );
        hologramPlane.position.set(0, 0, 0);
        group.add(hologramPlane);
        node.hologramPlane = hologramPlane;
      }

      nodeGroup.add(group);
      node.threeGroup = group;

      if (node.parentId) {
        const lineMesh = new THREE.Line(
          new THREE.BufferGeometry(),
          new THREE.LineBasicMaterial({ color: theme.glowColor, transparent: true, opacity: 0 })
        );
        // Perf: allocate the position buffer once (curve = 24 segments -> 25
        // points -> 75 floats) and reuse it every frame, instead of creating a
        // fresh Float32Array + BufferAttribute on each animation tick.
        const LINE_POINT_COUNT = 25;
        const linePositions = new Float32Array(LINE_POINT_COUNT * 3);
        lineMesh.geometry.setAttribute(
          "position",
          new THREE.BufferAttribute(linePositions, 3)
        );
        node.linePositions = linePositions;
        connectionGroup.add(lineMesh);
        node.lineMesh = lineMesh;

        const partMesh = new THREE.Mesh(
          node.level === 1 ? particleGeomL1 : particleGeomSmall,
          new THREE.MeshBasicMaterial({ color: theme.glowColor, transparent: true, opacity: 0, blending: THREE.AdditiveBlending })
        );
        particleGroup.add(partMesh);
        node.particleMesh = partMesh;
        node.particleProgress = Math.random();
      }
    });

    // Raycast click & hover
    const raycaster = new THREE.Raycaster();
    const mouse = new THREE.Vector2();

    const handleCanvasClick = (e) => {
      if (controls.state !== -1) return;

      const rect = renderer.domElement.getBoundingClientRect();
      mouse.x = ((e.clientX - rect.left) / rect.width) * 2 - 1;
      mouse.y = -((e.clientY - rect.top) / rect.height) * 2 + 1;

      raycaster.setFromCamera(mouse, camera);
      const intersects = raycaster.intersectObjects(nodeMeshes);

      if (intersects.length > 0) {
        const clickedMesh = intersects[0].object;
        const nodeId = clickedMesh.userData.nodeId;
        handleNodeClick(nodeId);
      }
    };

    const handleCanvasMouseMove = (e) => {
      const rect = renderer.domElement.getBoundingClientRect();
      mouse.x = ((e.clientX - rect.left) / rect.width) * 2 - 1;
      mouse.y = -((e.clientY - rect.top) / rect.height) * 2 + 1;

      raycaster.setFromCamera(mouse, camera);
      const intersects = raycaster.intersectObjects(nodeMeshes);

      if (intersects.length > 0) {
        const hoveredMesh = intersects[0].object;
        const nodeId = hoveredMesh.userData.nodeId;
        if (hoveredNodeIdRef.current !== nodeId) {
          hoveredNodeIdRef.current = nodeId;
          setHoveredNodeId(nodeId);
        }
        document.body.style.cursor = "pointer";
      } else if (hoveredNodeIdRef.current !== null) {
        hoveredNodeIdRef.current = null;
        setHoveredNodeId(null);
        document.body.style.cursor = "default";
      } else {
        document.body.style.cursor = "default";
      }
    };

    renderer.domElement.addEventListener("click", handleCanvasClick);
    renderer.domElement.addEventListener("mousemove", handleCanvasMouseMove);

    // Animation Loop
    let animationFrameId;
    const clock = new THREE.Clock();

    const animate = () => {
      animationFrameId = requestAnimationFrame(animate);
      // Bail if the container detached mid-flight (unmount / view toggle / treeData change);
      // a frame can still fire after React nulls the ref, before cleanup cancels the loop.
      if (!containerRef.current) return;
      const deltaTime = clock.getDelta();
      const time = clock.getElapsedTime();

      controls.update();

      // Update node visual groups & parameters (read directly from nodesRef.current)
      nodesRef.current.forEach((node) => {
        if (!node.threeGroup) return;

        // Visibility-skip: collapsed/hidden nodes do no per-frame work (transform
        // copies, ring spins, S-curve rebuilds). Hide their group + line + particle
        // and bail early. Animating nodes keep visible=true until the collapse tween
        // completes, so the shrink animation is preserved.
        const nodeActive = node.visible || node.level === 0;
        if (!nodeActive) {
          if (node.threeGroup.visible) node.threeGroup.visible = false;
          if (node.lineMesh && node.lineMesh.visible) {
            node.lineMesh.visible = false;
            node.lineMesh.material.opacity = 0;
          }
          if (node.particleMesh && node.particleMesh.visible) {
            node.particleMesh.visible = false;
            node.particleMesh.material.opacity = 0;
          }
          return;
        }

        node.threeGroup.visible = true;

        const isHovered = hoveredNodeIdRef.current === node.id;
        const hoverScale = isHovered ? 1.18 : 1.0;

        node.threeGroup.position.copy(node.currentPos);
        node.threeGroup.scale.setScalar(node.scale * hoverScale);

        if (node.coreMesh && node.coreMesh.material) {
          node.coreMesh.material.emissiveIntensity = isHovered 
            ? 0.28 
            : activeNodeIdRef.current === node.id 
              ? 0.18 
              : 0.06;
        }

        // Inner spins & rings animations
        if (node.hologramPlane && node.level === 1) {
          node.hologramPlane.rotation.y += deltaTime * 0.45;
          node.hologramPlane.rotation.x = Math.sin(time * 1.5) * 0.12;
        }
        if (node.orbitingRing1) {
          node.orbitingRing1.rotation.y += deltaTime * 0.2;
          node.orbitingRing1.rotation.x += deltaTime * 0.1;
        }
        if (node.orbitingRing2) {
          node.orbitingRing2.rotation.y -= deltaTime * 0.25;
          node.orbitingRing2.rotation.z += deltaTime * 0.15;
        }
        if (node.orbitingTorus) {
          node.orbitingTorus.rotation.y += deltaTime * 0.35;
        }
        if (node.orbitingRing) {
          node.orbitingRing.rotation.y += deltaTime * 0.4;
        }
        if (node.electronMesh) {
          node.electronMesh.position.set(
            Math.cos(time * 3.0) * 0.72,
            Math.sin(time * 3.0) * 0.72,
            Math.sin(time * 1.5) * 0.2
          );
        }

        // Bobbing category cubes
        if (node.level === 1) {
          const indexOffset = node.index || 0;
          node.threeGroup.position.y = node.currentPos.y + Math.sin(time * 1.2 + indexOffset * 1.5) * 0.12;
        }

        // Connection S-curves drawing
        if (node.parentId) {
          const parent = nodeByIdRef.current.get(node.parentId);
          if (parent && node.lineMesh) {
            const isVisible = node.visible && node.opacity > 0.02;
            node.lineMesh.visible = isVisible;

            if (isVisible) {
              const exitOffset = getExitOffset(parent.level);
              const enterOffset = getEnterOffset(node.level);

              const p1 = new THREE.Vector3(parent.currentPos.x, parent.currentPos.y - exitOffset, parent.currentPos.z);
              const p2 = new THREE.Vector3(node.currentPos.x, node.currentPos.y + enterOffset, node.currentPos.z);

              const midY = (p1.y + p2.y) / 2;
              const curve = new THREE.CubicBezierCurve3(
                p1,
                new THREE.Vector3(p1.x, midY, p1.z),
                new THREE.Vector3(p2.x, midY, p2.z),
                p2
              );

              const points = curve.getPoints(24);
              const linePositions = node.linePositions;
              for (let i = 0; i < points.length; i++) {
                const p = points[i];
                const o = i * 3;
                linePositions[o] = p.x;
                linePositions[o + 1] = p.y;
                linePositions[o + 2] = p.z;
              }

              node.lineMesh.geometry.attributes.position.needsUpdate = true;
              node.lineMesh.material.opacity = node.opacity * 0.28;

              if (node.particleMesh) {
                node.particleMesh.visible = isVisible;
                node.particleProgress += deltaTime * 0.25;
                if (node.particleProgress > 1.0) node.particleProgress = 0;

                const progressPos = curve.getPointAt(node.particleProgress);
                node.particleMesh.position.copy(progressPos);
                node.particleMesh.material.opacity = node.opacity * 0.9;
                node.particleMesh.scale.setScalar(node.scale * (isHovered ? 1.25 : 1.0));
              }
            } else {
              node.lineMesh.visible = false;
              node.lineMesh.material.opacity = 0;
              if (node.particleMesh) {
                node.particleMesh.visible = false;
                node.particleMesh.material.opacity = 0;
              }
            }
          }
        }
      });

      // Project coordinates to screenspace labels
      const tempV = new THREE.Vector3();
      const parentRect = containerRef.current.getBoundingClientRect();
      const widthHalf = parentRect.width / 2;
      const heightHalf = parentRect.height / 2;
      const cameraDistance = controls
        ? camera.position.distanceTo(controls.target)
        : 0;

      nodesRef.current.forEach((node) => {
        // Read the cached label element instead of querying the DOM every frame.
        const domElement = labelElementsRef.current.get(node.id);

        if (!domElement) return;

        const isFocusedLabel =
          activeNodeIdRef.current === node.id || hoveredNodeIdRef.current === node.id;
        const hideAtDistance = LABEL_HIDE_DISTANCE[node.level] || Infinity;
        const isTooZoomedOut = cameraDistance > hideAtDistance && !isFocusedLabel;
        const isVisible =
          node.visible &&
          node.opacity > 0.08 &&
          node.scale > 0.08 &&
          !isTooZoomedOut;

        if (isVisible) {
          tempV.copy(node.currentPos);
          tempV.project(camera);

          const isBehindCamera = tempV.z > 1.0;

          if (isBehindCamera) {
            domElement.style.display = "none";
          } else {
            let x = (tempV.x * widthHalf) + widthHalf;
            let y = -(tempV.y * heightHalf) + heightHalf;
            const labelWidth = isFocusedLabel ? 174 : 150;

            // screenspace offsets
            x -= labelWidth / 2;
            if (node.level === 0) {
              y += 50;
            } else if (node.level === 1) {
              y += 45;
            } else {
              y += 28;
            }

            if (x >= 0 && x <= parentRect.width && y >= 0 && y <= parentRect.height) {
              domElement.style.display = "block";
              domElement.style.transform = `translate3d(${x}px, ${y}px, 0)`;
              domElement.style.opacity = node.opacity;
            } else {
              domElement.style.display = "none";
            }
          }
        } else {
          domElement.style.display = "none";
        }
      });

      renderer.render(scene, camera);
    };

    animate();

    const handleResize = () => {
      if (!containerRef.current || !threeObjectsRef.current.renderer) return;
      const w = containerRef.current.clientWidth;
      const h = containerRef.current.clientHeight;

      const cam = threeObjectsRef.current.camera;
      const ren = threeObjectsRef.current.renderer;

      cam.aspect = w / h;
      cam.updateProjectionMatrix();
      ren.setSize(w, h);
    };

    window.addEventListener("resize", handleResize);

    return () => {
      cancelAnimationFrame(animationFrameId);
      window.removeEventListener("resize", handleResize);
      document.body.style.cursor = "default";
      if (renderer && renderer.domElement) {
        renderer.domElement.removeEventListener("click", handleCanvasClick);
        renderer.domElement.removeEventListener("mousemove", handleCanvasMouseMove);
      }
      if (controls) {
        controls.dispose();
      }
      scene.traverse((object) => {
        // Shared geometries are referenced by many meshes; skip them here and
        // dispose each exactly once below to avoid redundant dispose() calls.
        if (object.geometry && !sharedGeometrySet.has(object.geometry)) {
          object.geometry.dispose();
        }

        const materials = Array.isArray(object.material)
          ? object.material
          : object.material
            ? [object.material]
            : [];

        materials.forEach((material) => {
          Object.values(material).forEach((value) => {
            if (value && value.isTexture) {
              value.dispose();
            }
          });
          material.dispose();
        });
      });

      // Dispose the shared geometries once.
      sharedGeometries.forEach((g) => g.dispose());

      if (renderer) {
        renderer.dispose();
      }
    };
  }, [treeData]);

  // Click & Expand handler (mutates nodesRef and triggers shallow state clone to update HTML labels)
  const handleNodeClick = (nodeId) => {
    const node = nodesRef.current.find((n) => n.id === nodeId);
    if (!node) return;

    activeNodeIdRef.current = nodeId;
    setActiveNode(node);

    if (node.type === "pending_scenario") {
      focusCamera(node);
      return;
    }

    if (node.type === "scenario") {
      const targetId = node.rawNode?.slug || node.rawNode?.id || node.id;
      navigate(`/lab/${targetId}`, {
        state: {
          title: node.rawNode?.title,
          cve: node.rawNode?.cve,
          description: node.rawNode?.description,
          difficulty: node.rawNode?.difficulty,
          time_estimate: node.rawNode?.time_estimate,
          learning_objectives: node.rawNode?.learning_objectives,
          // Carry the saved topology so the Lab page can show the Topology tab
          // instantly on navigation, before the catalog tree finishes loading.
          topology_json: node.rawNode?.topology_json
        }
      });
      return;
    }

    const willExpand = !node.expanded;

    if (willExpand) {
      expandNode(node);
    } else {
      collapseNode(node);
    }

    focusCamera(node, willExpand);

    setFlatNodes([...nodesRef.current]);
  };

  const expandNode = (node) => {
    node.expanded = true;

    const children = nodesRef.current.filter((n) => n.parentId === node.id);
    children.forEach((child) => {
      child.visible = true;
      child.currentPos.copy(node.currentPos);
      child.scale = 0;
      child.opacity = 0;
      // Seed x/y/z so gsap has a numeric start value; otherwise the first tween
      // begins from undefined → NaN, corrupting currentPos and the line geometry.
      child.x = node.currentPos.x;
      child.y = node.currentPos.y;
      child.z = node.currentPos.z;

      gsap.killTweensOf(child);
      gsap.to(child, {
        scale: 1,
        opacity: 1,
        x: child.targetPos.x,
        y: child.targetPos.y,
        z: child.targetPos.z,
        duration: 1.15,
        ease: "power2.out",
        onUpdate: () => {
          child.currentPos.set(child.x, child.y, child.z);
        }
      });
    });

    const siblings = nodesRef.current.filter(
      (n) => n.parentId === node.parentId && n.id !== node.id
    );
    siblings.forEach((sibling) => {
      if (sibling.expanded) {
        collapseNode(sibling);
      }
    });
  };

  const collapseNode = (node) => {
    node.expanded = false;

    const children = nodesRef.current.filter((n) => n.parentId === node.id);
    children.forEach((child) => {
      if (child.expanded) {
        collapseNode(child);
      }

      // Seed x/y/z from the child's current location so the tween starts from a
      // numeric value (a child collapsed before ever expanding has no x/y/z yet).
      child.x = child.currentPos.x;
      child.y = child.currentPos.y;
      child.z = child.currentPos.z;

      gsap.killTweensOf(child);
      gsap.to(child, {
        scale: 0,
        opacity: 0,
        x: node.currentPos.x,
        y: node.currentPos.y,
        z: node.currentPos.z,
        duration: 0.75,
        ease: "power2.inOut",
        onUpdate: () => {
          child.currentPos.set(child.x, child.y, child.z);
        },
        onComplete: () => {
          child.visible = false;
        }
      });
    });
  };

  const focusCamera = (node, willExpand = node.expanded) => {
    const { camera, controls } = threeObjectsRef.current;
    if (!camera || !controls) return;

    // Adjust vertical framing: if expanding, offset Y slightly downwards to include children
    let focusY = node.targetPos.y;
    if (willExpand && node.level < 3) {
      focusY -= 2.2;
    }

    const targetX = node.targetPos.x;
    const targetY = focusY;
    const targetZ = node.targetPos.z;

    const distanceOffset = node.level === 0 ? 21 : node.level === 1 ? 14 : 8.5;
    
    // Position camera straight on, parallel to Z-plane
    const targetCameraPos = new THREE.Vector3(
      targetX,
      targetY + 2.5,
      targetZ + distanceOffset
    );

    // Disable controls and damping temporarily during tween to prevent conflicts
    controls.enabled = false;
    controls.enableDamping = false;

    gsap.killTweensOf(controls.target);
    gsap.killTweensOf(camera.position);

    const tl = gsap.timeline({
      onComplete: () => {
        controls.enabled = true;
        controls.enableDamping = true;
      }
    });

    tl.to(controls.target, {
      x: targetX,
      y: targetY,
      z: targetZ,
      duration: 1.15,
      ease: "power2.inOut",
      onUpdate: () => {
        controls.update();
      }
    }, 0);

    tl.to(camera.position, {
      x: targetCameraPos.x,
      y: targetCameraPos.y,
      z: targetCameraPos.z,
      duration: 1.15,
      ease: "power2.inOut",
      onUpdate: () => {
        controls.update();
      }
    }, 0);
  };

  const handleZoom = (direction) => {
    const { camera, controls } = threeObjectsRef.current;
    if (!camera || !controls) return;

    const target = controls.target;
    const zoomFactor = direction > 0 ? 0.78 : 1.25;

    const offset = new THREE.Vector3().copy(camera.position).sub(target);
    offset.multiplyScalar(zoomFactor);

    const dist = offset.length();
    if (dist < 4.5 && direction > 0) return;
    if (dist > 55 && direction < 0) return;

    gsap.to(camera.position, {
      x: target.x + offset.x,
      y: target.y + offset.y,
      z: target.z + offset.z,
      duration: 0.45,
      ease: "power2.out",
      onUpdate: () => {
        controls.update();
      }
    });
  };

  const resetView = () => {
    const rootNode = flatNodes.find((n) => n.level === 0);
    if (rootNode) {
      flatNodes.forEach((node) => {
        gsap.killTweensOf(node);

        if (node.level > 0) {
          node.visible = false;
          node.expanded = false;
          node.scale = 0;
          node.opacity = 0;
          node.currentPos.copy(node.targetPos);
          if (node.threeGroup) {
            node.threeGroup.visible = false;
          }
          if (node.lineMesh) {
            node.lineMesh.visible = false;
            node.lineMesh.material.opacity = 0;
          }
          if (node.particleMesh) {
            node.particleMesh.visible = false;
            node.particleMesh.material.opacity = 0;
          }
        } else if (node.level === 0) {
          node.expanded = false;
          node.scale = 1;
          node.opacity = 1;
          node.visible = true;
          node.currentPos.copy(node.targetPos);
          if (node.threeGroup) {
            node.threeGroup.visible = true;
          }
        }
      });

      setFlatNodes([...flatNodes]);
      setActiveNode(rootNode);
      activeNodeIdRef.current = rootNode.id;
      focusCamera(rootNode);
    }
  };

  const nodeInsights = useMemo(() => {
    const childrenByParent = new Map();
    flatNodes.forEach((node) => {
      if (!node.parentId) return;
      const siblings = childrenByParent.get(node.parentId) || [];
      siblings.push(node);
      childrenByParent.set(node.parentId, siblings);
    });

    const collectDescendants = (nodeId) => {
      const children = childrenByParent.get(nodeId) || [];
      return children.reduce(
        (all, child) => all.concat(child, collectDescendants(child.id)),
        []
      );
    };

    const insights = new Map();

    flatNodes.forEach((node) => {
      const directChildren = childrenByParent.get(node.id) || [];
      const descendants = collectDescendants(node.id);
      const scenarios = descendants.filter((item) =>
        item.type === "scenario" || item.type === "pending_scenario"
      );
      const categories = descendants.filter((item) => item.type === "category");
      const pendingCount = descendants.filter((item) => item.type === "pending_scenario").length;
      const difficultySet = new Set(
        scenarios
          .map((item) => item.difficulty)
          .filter(Boolean)
          .map((item) => String(item).trim())
      );
      const estimatedHours = scenarios.reduce(
        (total, item) => total + parseHours(item.time_estimate),
        0
      );
      const profile = getCategoryProfile(node.title);
      const readiness = makeReadinessScore(node, scenarios.length, directChildren.length);
      const childPreview = directChildren.map((child, childIndex) => ({
        id: child.id || `${node.id}-child-${childIndex}`,
        title: cleanTitle(child.title)
      }));

      insights.set(node.id, {
        categories: categories.length,
        childCount: directChildren.length,
        childPreview,
        difficulty: difficultySet.size ? Array.from(difficultySet).slice(0, 3) : ["Guided"],
        estimatedHours,
        pendingCount,
        profile,
        readiness,
        scenarioCount: scenarios.length,
        status: pendingCount > 0 ? "Requests pending" : scenarios.length > 0 ? "Ready to launch" : "Catalog track"
      });
    });

    return insights;
  }, [flatNodes]);

  const activeInsight = activeNode ? nodeInsights.get(activeNode.id) : null;
  const visibleLevelOne = flatNodes.filter((node) => node.level === 1 && node.visible);
  const totalScenarios = flatNodes.filter((node) => node.type === "scenario" || node.type === "pending_scenario").length;
  const pendingScenarios = flatNodes.filter((node) => node.type === "pending_scenario").length;
  const averageReadiness = visibleLevelOne.length
    ? Math.round(
        visibleLevelOne.reduce((total, node) => total + (nodeInsights.get(node.id)?.readiness || 0), 0) /
          visibleLevelOne.length
      )
    : activeInsight?.readiness || 0;

  return (
    <div
      ref={containerRef}
      className="relative w-full h-155 md:h-180 rounded-2xl border border-slate-800/90 overflow-hidden select-none shadow-[0_22px_80px_rgba(0,0,0,0.34)]"
      style={{ background: PANEL_BG }}
    >
      <canvas ref={canvasRef} className="block w-full h-full cursor-grab active:cursor-grabbing z-1" />
      <div className="pointer-events-none absolute inset-0 z-2 bg-[radial-gradient(circle_at_50%_18%,rgba(45,212,191,0.12),transparent_34%),linear-gradient(180deg,rgba(255,255,255,0.035),transparent_22%,rgba(0,0,0,0.22))]" />

      {/* HTML Screenspace labels overlay */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden select-none z-10">
        {flatNodes.map((node) => {
          const { glowColor, typeLabel } = getNodeTheme(node.type, node.title, node.difficulty);
          const hexGlowColor = `#${glowColor.toString(16).padStart(6, "0")}`;
          const isCategory = node.type === "category" || node.level === 0;
          const isPending = node.type === "pending_scenario";
          const childCount = node.childrenIds?.length || 0;
          const isCurrentActive = activeNode?.id === node.id && node.level > 0;
          const isHovered = hoveredNodeId === node.id;
          const insight = nodeInsights.get(node.id);
          const contentCount = insight?.scenarioCount || childCount;
          const readiness = insight?.readiness || 0;
          const profile = insight?.profile || getCategoryProfile(node.title);

          const isLevel1 = node.level === 1;
          const isLevel0 = node.level === 0;

          return (
            <div
              key={node.id}
              data-node-id={node.id}
              className="absolute left-0 top-0 pointer-events-auto origin-center select-none"
              style={{ display: "none", willChange: "transform, opacity" }}
            >
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  handleNodeClick(node.id);
                }}
                onMouseEnter={() => setHoveredNodeId(node.id)}
                onMouseLeave={() => setHoveredNodeId(null)}
                className={`flex flex-col items-center text-center transition-all duration-300 pointer-events-auto cursor-pointer rounded-lg ${isCurrentActive || isHovered ? "w-43.5" : "w-37.5"}
                  ${
                    isCurrentActive || isHovered
                      ? "p-2.5 border bg-slate-950/88 backdrop-blur-md shadow-2xl scale-102"
                      : "p-1.5 border border-slate-900/15 bg-slate-950/20 hover:bg-slate-950/45 backdrop-blur-[1.5px] scale-100"
                  }
                `}
                style={{
                  boxShadow: isCurrentActive || isHovered ? `0 0 15px ${hexGlowColor}35` : "none",
                  borderColor: isCurrentActive || isHovered ? hexGlowColor : "rgba(30, 41, 59, 0.2)"
                }}
              >
                {(isHovered || isCurrentActive) ? (
                  /* Expanded HUD View */
                  <>
                    <div className="flex items-center justify-between w-full text-[8px] font-extrabold tracking-widest uppercase text-slate-500 border-b border-slate-900/65 pb-1 mb-1.5 gap-2">
                      <span>{profile.badge || typeLabel}</span>
                      {isCategory ? (
                        <span className="text-emerald-400 font-bold">{contentCount} Items</span>
                      ) : (
                        <span className="px-1 py-0.2 rounded font-extrabold" style={{ color: hexGlowColor, backgroundColor: `${hexGlowColor}15` }}>
                          {node.difficulty || "Box"}
                        </span>
                      )}
                    </div>
                    <span className="text-[10.5px] font-bold text-white leading-tight line-clamp-2 w-full wrap-break-word select-none pointer-events-none drop-shadow-[0_2px_4px_rgba(0,0,0,0.8)]">
                      {cleanTitle(node.title)}
                    </span>
                    {isLevel0 && (
                      <span className="text-[8px] text-emerald-400/80 mt-0.5 uppercase tracking-wider font-semibold">
                        Cyber Range Center
                      </span>
                    )}
                    {!isLevel0 && (
                      <div className="grid grid-cols-2 gap-1.5 w-full mt-2 text-[8px] font-bold">
                        <span className="rounded border border-slate-800/80 bg-slate-900/70 px-1.5 py-1 text-slate-300">
                          {readiness}% Ready
                        </span>
                        <span className="rounded border border-slate-800/80 bg-slate-900/70 px-1.5 py-1" style={{ color: hexGlowColor }}>
                          {insight?.estimatedHours ? `${insight.estimatedHours}h` : insight?.difficulty?.[0] || "Guided"}
                        </span>
                      </div>
                    )}
                    {!isLevel0 && (
                      <div className="flex items-center justify-center gap-1.5 w-full pt-1.5 border-t border-slate-900 mt-1.5">
                        <span className="text-[8.5px] text-emerald-400 flex items-center gap-1 font-bold">
                          {isPending ? (
                            <>
                              <ShieldAlert className="h-2.5 w-2.5 text-amber-500 animate-pulse" />
                              PENDING
                            </>
                          ) : (
                            <>
                              <Play className="h-2.5 w-2.5 text-emerald-400 fill-emerald-400" />
                              START BOX
                            </>
                          )}
                        </span>
                      </div>
                    )}
                  </>
                ) : (
                  /* Minimal Floating Title View */
                  <>
                    <span 
                      className="text-[9.5px] font-bold leading-tight line-clamp-2 w-full wrap-break-word select-none pointer-events-none transition-colors duration-250"
                      style={{ 
                        color: isLevel0 ? "#00ffcc" : "#e2e8f0",
                        textShadow: "0 2px 5px rgba(0,0,0,0.95)"
                      }}
                    >
                      {cleanTitle(node.title)}
                    </span>
                    <span 
                      className="w-1.5 h-1.5 rounded-full mt-1.5 shadow-[0_0_6px_currentColor]"
                      style={{ 
                        color: hexGlowColor,
                        backgroundColor: "currentColor"
                      }} 
                    />
                  </>
                )}
              </button>
            </div>
          );
        })}
      </div>

      {/* Floating Center-bottom zoom icons */}
      <div className="absolute bottom-5 left-1/2 -translate-x-1/2 flex items-center gap-2 bg-slate-950/80 border border-emerald-500/15 backdrop-blur-md px-2.5 py-1.5 rounded-xl pointer-events-auto z-20 shadow-xl">
        <button
          type="button"
          onClick={() => handleZoom(-1)}
          className="cursor-pointer w-8 h-8 flex items-center justify-center rounded-lg bg-slate-900/60 border border-slate-800/80 text-slate-300 hover:text-emerald-400 hover:border-emerald-400/40 hover:bg-slate-900 transition select-none"
          title="Zoom out"
        >
          <ZoomOut className="h-4 w-4" />
        </button>
        <button
          type="button"
          onClick={() => handleZoom(1)}
          className="cursor-pointer w-8 h-8 flex items-center justify-center rounded-lg bg-slate-900/60 border border-slate-800/80 text-slate-300 hover:text-emerald-400 hover:border-emerald-400/40 hover:bg-slate-900 transition select-none"
          title="Zoom in"
        >
          <ZoomIn className="h-4 w-4" />
        </button>
      </div>

      {/* Header telemetry and layout tools */}
      <div className="absolute top-5 left-5 right-5 flex items-center justify-between pointer-events-none z-20">
        <div className="flex items-center gap-3 bg-slate-950/80 border border-emerald-500/15 backdrop-blur-md px-4 py-2 rounded-xl pointer-events-auto">
          <div className="p-1.5 rounded-lg bg-emerald-500/10 border border-emerald-500/25 text-emerald-400">
            <Network className="h-4.5 w-4.5" />
          </div>
          <div>
            <h3 className="text-[11px] font-bold text-white uppercase tracking-wider">
              Practice Challenges
            </h3>
            <p className="text-[9px] text-slate-400 tracking-normal font-semibold">
              Structured learning path explorer
            </p>
          </div>
        </div>

        <div className="hidden lg:grid grid-cols-4 gap-2 pointer-events-auto">
          {[
            { label: "Tracks", value: visibleLevelOne.length, icon: Layers3 },
            { label: "Boxes", value: totalScenarios, icon: Target },
            { label: "Ready", value: `${averageReadiness}%`, icon: Activity },
            { label: "Pending", value: pendingScenarios, icon: ShieldAlert }
          ].map((metric) => {
            const Icon = metric.icon;
            return (
              <div
                key={metric.label}
                className="min-w-20.5 rounded-lg border border-slate-800/80 bg-slate-950/76 px-2.5 py-2 backdrop-blur-md"
              >
                <div className="flex items-center gap-1.5 text-slate-500">
                  <Icon className="h-3 w-3" />
                  <span className="text-[8px] font-extrabold uppercase tracking-wider">{metric.label}</span>
                </div>
                <div className="mt-0.5 text-sm font-black text-white">{metric.value}</div>
              </div>
            );
          })}
        </div>

        <button
          type="button"
          onClick={resetView}
          className="cursor-pointer inline-flex items-center gap-2 bg-slate-950/80 border border-emerald-500/15 backdrop-blur-md text-slate-300 hover:text-emerald-400 hover:border-emerald-400/40 transition px-3.5 py-1.5 rounded-xl text-[10px] font-bold uppercase tracking-wider pointer-events-auto"
        >
          <RotateCcw className="h-3.5 w-3.5" />
          Reset
        </button>
      </div>

      {/* Active track intelligence */}
      {activeNode && activeInsight && (
        <aside className="absolute top-24 right-5 hidden xl:block w-71.5 rounded-xl border border-slate-800/80 bg-slate-950/78 backdrop-blur-md p-4 z-20 shadow-2xl pointer-events-auto">
          <div className="flex items-start justify-between gap-3">
            <div>
              <div className="text-[9px] font-black uppercase tracking-[0.18em] text-emerald-400">
                {activeInsight.profile.badge} Command View
              </div>
              <h4 className="mt-1 text-sm font-black leading-tight text-white">
                {cleanTitle(activeNode.title)}
              </h4>
            </div>
            <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg border border-emerald-400/25 bg-emerald-400/10 text-emerald-300">
              <Activity className="h-5 w-5" />
            </div>
          </div>

          <p className="mt-3 text-[11px] leading-5 text-slate-300">
            {activeNode.description || activeInsight.profile.summary}
          </p>

          <div className="mt-4 grid grid-cols-3 gap-2">
            <div className="rounded-lg border border-slate-800/80 bg-slate-900/60 p-2">
              <div className="text-[8px] font-extrabold uppercase tracking-wider text-slate-500">Items</div>
              <div className="mt-1 text-lg font-black text-white">
                {activeInsight.scenarioCount || activeInsight.childCount}
              </div>
            </div>
            <div className="rounded-lg border border-slate-800/80 bg-slate-900/60 p-2">
              <div className="text-[8px] font-extrabold uppercase tracking-wider text-slate-500">Ready</div>
              <div className="mt-1 text-lg font-black text-emerald-300">{activeInsight.readiness}%</div>
            </div>
            <div className="rounded-lg border border-slate-800/80 bg-slate-900/60 p-2">
              <div className="text-[8px] font-extrabold uppercase tracking-wider text-slate-500">Time</div>
              <div className="mt-1 text-lg font-black text-cyan-200">
                {activeInsight.estimatedHours || "--"}h
              </div>
            </div>
          </div>

          <div className="mt-4 space-y-2">
            <div className="flex items-center justify-between text-[10px] font-bold text-slate-400">
              <span>{activeInsight.profile.outcome}</span>
              <span className="text-emerald-300">{activeInsight.status}</span>
            </div>
            <div className="h-1.5 overflow-hidden rounded-full bg-slate-800">
              <div
                className="h-full rounded-full bg-linear-to-r from-emerald-400 via-cyan-300 to-blue-400"
                style={{ width: `${activeInsight.readiness}%` }}
              />
            </div>
          </div>

          <div className="mt-4 flex flex-wrap gap-1.5">
            {activeInsight.difficulty.map((tag) => (
              <span
                key={tag}
                className="rounded-md border border-cyan-300/20 bg-cyan-300/10 px-2 py-1 text-[9px] font-extrabold uppercase tracking-wider text-cyan-100"
              >
                {tag}
              </span>
            ))}
          </div>

          {activeInsight.childPreview.length > 0 && (
            <div className="mt-4 border-t border-slate-800/80 pt-3">
              <div className="mb-2 flex items-center justify-between gap-2 text-[9px] font-black uppercase tracking-wider text-slate-500">
                <span className="flex items-center gap-1.5">
                  <Clock3 className="h-3 w-3" />
                  Next Nodes
                </span>
                <span>{activeInsight.childPreview.length}</span>
              </div>
              <div className="max-h-37 space-y-1.5 overflow-y-auto pr-1 [scrollbar-color:rgba(16,185,129,0.55)_rgba(15,23,42,0.65)] scrollbar-thin [&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-emerald-500/50 [&::-webkit-scrollbar-track]:bg-slate-950/60">
                {activeInsight.childPreview.map((child, childIndex) => (
                  <div
                    key={`${child.id}-${childIndex}`}
                    className="truncate rounded-md border border-slate-800/70 bg-slate-900/50 px-2 py-1.5 text-[10px] font-bold text-slate-200"
                  >
                    {child.title}
                  </div>
                ))}
              </div>
            </div>
          )}

          {showCustomRequestButton && (
            <div className="mt-4 border-t border-slate-800/80 pt-4 flex flex-col gap-2">
              <button
                type="button"
                className="w-full cursor-pointer inline-flex items-center justify-center gap-2 rounded-lg border border-emerald-500 bg-emerald-500/10 px-4 py-2.5 text-xs font-bold uppercase tracking-wider text-emerald-400 shadow-[0_0_15px_rgba(16,185,129,0.15)] transition-all duration-300 hover:bg-emerald-500 hover:text-slate-950 hover:shadow-[0_0_25px_rgba(16,185,129,0.4)] hover:-translate-y-px"
                onClick={() => onRequestCustom?.(activeNode.id, isMultiVMLabs, isBuildYourOwn)}
              >
                {isMultiVMLabs ? "Request Lab" : "Request Custom Version"}
                <ArrowRight className="h-4 w-4" />
              </button>
            </div>
          )}
        </aside>
      )}

      {/* Interactive hints */}
      <div className="absolute bottom-5 left-5 hidden sm:flex bg-slate-950/72 border border-slate-800/70 backdrop-blur-xs px-3.5 py-2 rounded-xl text-[9px] text-slate-400 pointer-events-none flex-col gap-1 z-20 font-medium tracking-wide">
        <div className="flex items-center gap-1.5">
          <span className="w-1.5 h-1.5 rounded-full bg-slate-500" />
          <span><strong className="text-slate-300">Left Click + Drag:</strong> Pan / Move View</span>
        </div>
        <div className="flex items-center gap-1.5">
          <span className="w-1.5 h-1.5 rounded-full bg-slate-500" />
          <span><strong className="text-slate-300">Scroll / Pinch:</strong> Zoom In / Out</span>
        </div>
        <div className="flex items-center gap-1.5">
          <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
          <span><strong className="text-slate-300">Click Cubes:</strong> Drill down / Focus</span>
        </div>
      </div>
    </div>
  );
}

